export default {
  activity_transport_01: {
    title: 'Jeg gikk eller syklet til jobb i dag',
    description:
      'Kan logges på dager hvor du for det meste bruker ren muskelkraft, som å gå, skate, sykle eller benytter andre transportformer som ikke involverer strøm eller drivstoff for å komme til jobb.',
    co2_explanation: `MARKDOWN:
## Fakta
- Ved å sykle eller gå kan du redusere antall biler på veien og dermed minske forurensning, støynivå, svevestøv, giftige gasser og samtidig bidra til et vakrere og mer levende bymiljø. I tillegg vil andre digge deg for at du tar det beste valget for kloden vår.

## Hva vi antar
- Besparelsen er beregnet ved å se på forskjellen på hvordan en gjennomsnittsperson i ditt land kommer seg til jobb og en som benytter ren muskelkraft til jobben. 
- Fra reisevaneundersøkelsen kan vi finne avstander og transportmidler som en gjennomsnittsperson benytter til arbeid. Den samme informasjonen er benyttet i vår personlige klimakalkulator. Her kan du finne mer informasjon og nøyaktig hvor mye du vil kunne spare gjennom året.`,
    points_explanation: `MARKDOWN:
Poengene skal gjenspeile hvor stor påvirkning en aktivitet har på omverdenen.

**5 poeng - individuell påvirkning**<br/>
For eksempel: aktiviteter du kan gjøre på egen hånd uten å måtte kommunisere med eller påvirke andre. 

**10 poeng - sosial påvirkning**<br/>
For eksempel: Å gi komplimenter, snakke med noen, inspirere, motivere venner, familie, kollegaer eller en lokal butikk. 

**15 poeng - lokal påvirkning**<br/>
For eksempel: Å gi komplimenter, snakke med, inspirere eller motivere samfunnsorgan som organisasjoner, politikere, kommuner, osv.`,
    health_explanation: `MARKDOWN:
## Fakta
- Ved å gå eller sykle til jobb vil du mest sannsynlig ha en bedre start på dagen samtidig som at du sniker inn en liten treningsøkt.`,
    biodiversity_explanation: `MARKDOWN:
## Fakta
- Den største kilden til mikroplast i Norge er slitasje av bildekk. Det er fordi dekk består av ca. 60% syntetisk materiale. Årlig dannes det ca. 5000 tonn mikroplast fra slitasje av bildekk, og ca. halvparten av dette havner i havet. Dermed er det å redusere bilbruken et stort bidrag til å redusere mikroplastutslipp!`,
  },
  activity_transport_02: {
    title: 'Jeg tok kollektivtransport til jobb i dag',
    description:
      'Kan logges på dager hvor du for det meste bruker offentlig transport som buss, trikk eller tog for å komme til jobb.',
    co2_explanation: `MARKDOWN:
## Fakta
- Mange byer i verden har forbud mot biler i sentrum. Dette har ført til mindre utslipp, bedre luftkvalitet, mer liv i bykjernen og mindre ulykker. 

## Hva vi antar 
- Besparelsen er beregnet ved å se på forskjellen på hvordan en gjennomsnittsperson i ditt land kommer seg til jobb og en som benytter ren kollektivtransport til jobben. 
- Fra reisevaneundersøkelsen kan vi finne avstander og transportmidler som en gjennomsnittsperson benytter til arbeid. Den samme informasjonen er benyttet i vår personlige klimakalkulator. Her finner du mer informasjon og nøyaktig hvor mye du vil kunne spare gjennom året.

## Tilleggsinformasjon
- [https://www.theguardian.com/cities/2018/sep/18/paradise-life-spanish-city-banned-cars-pontevedra](https://www.theguardian.com/cities/2018/sep/18/paradise-life-spanish-city-banned-cars-pontevedra)`,
    points_explanation: `MARKDOWN:
Poengene skal gjenspeile hvor stor påvirkning en aktivitet har på omverdenen.

**5 poeng - individuell påvirkning**<br/>
For eksempel: aktiviteter du kan gjøre på egen hånd uten å måtte kommunisere med eller påvirke andre. 

**10 poeng - sosial påvirkning**<br/>
For eksempel: Å gi komplimenter, snakke med noen, inspirere, motivere venner, familie, kollegaer eller en lokal butikk. 

**15 poeng - lokal påvirkning**<br/>
For eksempel: Å gi komplimenter, snakke med, inspirere eller motivere samfunnsorgan som organisasjoner, politikere, kommuner, osv.`,
    health_explanation: `MARKDOWN:
## Fakta
- Å bruke kollektivtransport kan bidra til å fremme en mer aktiv livsstil og fjerne stress og angst knyttet til bilkjøring.
- Kollektivtransport har gode, sosiale fordeler der du kan glede deg over en rask, sikker og billig transportmåte, og få kontakt med familie, venner og kolleger.  
- Kollektivtransport er en måte å sikre sosial likhet på, der lavtlønnede, arbeidsledige, eldre og personer med nedsatt funksjonsevne er spesielt utsatt for sosial isolasjon som følge av dårlige transportalternativer.`,
    biodiversity_explanation: `MARKDOWN:
## Fakta
- Slitasje av bildekk fører til mikroplast i havet? Verdens naturvernunion – International Union for Conservation of Nature and Natural Resources (IUCN) – har funnet at opptil 30% av «plastsuppa» som forurenser havene kommer fra slitasje av tekstiler og bildekk.

## Tilleggsinformasjon
- [https://portals.iucn.org/library/node/46622](https://portals.iucn.org/library/node/46622)`,
  },
  activity_transport_03: {
    title: 'Jeg gikk eller syklet til en fritidsaktivitet i dag',
    description:
      'Kan logges på dager hvor du for det meste bruker ren muskelkraft, som å gå, skate, sykle eller benytter andre transportformer som ikke involverer strøm eller drivstoff for å komme til fritidsaktiviteter.',
    co2_explanation: `MARKDOWN:
## Fakta
- Veier og parkeringsplasser okkuperer enorme, naturlige landarealer som mange arter er avhengige av. Disse artene tør ikke ikke å krysse store veier og vi splitter derfor opp deres små, naturlige habitater. Ved å gå og sykle kan vi heller dele små og hyggelige veier med dyre- og plantelivet.

## Hva vi antar
- Besparelsen er beregnet ved å se på forskjellen på hvordan en gjennomsnittsperson i ditt land kommer seg til fritidsaktiviteter og en som benytter ren muskelkraft. 
- Fra reisevaneundersøkelsen kan vi finne avstander og transportmidler som en gjennomsnittsperson benytter til fritidsaktiviteter. Den samme informasjonen er benyttet i vår personlige klimakalkulator. Her finner du mer informasjon og nøyaktig hvor mye du vil kunne spare gjennom året.`,
    points_explanation: `MARKDOWN:
Poengene skal gjenspeile hvor stor påvirkning en aktivitet har på omverdenen.

**5 poeng - individuell påvirkning**<br/>
For eksempel: aktiviteter du kan gjøre på egen hånd uten å måtte kommunisere med eller påvirke andre. 

**10 poeng - sosial påvirkning**<br/>
For eksempel: Å gi komplimenter, snakke med noen, inspirere, motivere venner, familie, kollegaer eller en lokal butikk. 

**15 poeng - lokal påvirkning**<br/>
For eksempel: Å gi komplimenter, snakke med, inspirere eller motivere samfunnsorgan som organisasjoner, politikere, kommuner, osv.`,
    health_explanation: `MARKDOWN:
## Fakta
- Å sykle eller gå kan redusere risikoen for overvekt og diabetes.
- Det er anerkjent at fysisk aktivitet er bra for helsa!

## Tilleggsinformasjon
- [https://www.ncbi.nlm.nih.gov/pmc/articles/PMC2937005/](https://www.ncbi.nlm.nih.gov/pmc/articles/PMC2937005/)`,
    biodiversity_explanation: `MARKDOWN:
## Fakta
- Studier viser at personbiler er største kilden til mikroplast fra bildekk. Hvis man tar kollektivt, sykler går, eller samkjører i stedet for å kjøre bil alene kan man bidra stort til å redusere mikroplastutslipp fra norske veier!`,
  },
  activity_transport_04: {
    title: 'Jeg tok kollektivtransport til en fritidsaktivitet i dag',
    description:
      'Kan logges på dager hvor du for det meste bruker offentlig transport som buss, trikk eller tog for å komme til fritidsaktiviteter.',
    co2_explanation: `MARKDOWN:
## Fakta
- Mange byer i verden har forbud mot biler i sentrum. Dette har ført til mindre utslipp, bedre luftkvalitet, mer liv i bykjernen og mindre ulykker.

## Hva vi antar 
- Besparelsen er beregnet ved å se på forskjellen på hvordan en gjennomsnittsperson i ditt land kommer seg til fritidsaktiviteter og en som benytter kollektivtransport. 
- Fra reisevaneundersøkelsen kan vi finne avstander og transportmidler som en gjennomsnittsperson benytter til fritidsaktiviteter. Den samme informasjonen er benyttet i vår personlige klimakalkulator. Her kan du finne mer informasjon og nøyaktig hvor mye du vil kunne spare gjennom året.

## Tilleggsinformasjon
- [https://www.theguardian.com/cities/2018/sep/18/paradise-life-spanish-city-banned-cars-pontevedra](https://www.theguardian.com/cities/2018/sep/18/paradise-life-spanish-city-banned-cars-pontevedra)`,
    points_explanation: `MARKDOWN:
Poengene skal gjenspeile hvor stor påvirkning en aktivitet har på omverdenen.

**5 poeng - individuell påvirkning**<br/>
For eksempel: aktiviteter du kan gjøre på egen hånd uten å måtte kommunisere med eller påvirke andre. 

**10 poeng - sosial påvirkning**<br/>
For eksempel: Å gi komplimenter, snakke med noen, inspirere, motivere venner, familie, kollegaer eller en lokal butikk. 

**15 poeng - lokal påvirkning**<br/>
For eksempel: Å gi komplimenter, snakke med, inspirere eller motivere samfunnsorgan som organisasjoner, politikere, kommuner, osv.`,
    health_explanation: `MARKDOWN:
## Fakta
- Å bruke kollektivtransport kan bidra til å fremme en mer aktiv livsstil og fjerne stress og angst tilknyttet bilkjøring.
- Kollektivtransport har gode, sosiale fordeler der du kan glede deg over en rask, sikker og billig transportmåte, og få kontakt med familie, venner og kolleger.  
- Kollektivtransport er en måte å sikre sosial likhet på, der lavtlønnede, arbeidsledige, eldre og personer med nedsatt funksjonsevne er spesielt utsatt for sosial isolasjon som følge av dårlige transportalternativer.`,
    biodiversity_explanation: `MARKDOWN:
## Fakta
- Bykjøring fører til mer mikroplastutslipp enn kjøring andre steder. Dette er fordi man akselererer, bremser og svinger mer enn man gjør på motorveier og landeveiskjøring.

## Tilleggsinformasjon
- [https://www.miljodirektoratet.no/globalassets/publikasjoner/M959/M959.pdf](https://www.miljodirektoratet.no/globalassets/publikasjoner/M959/M959.pdf)`,
  },
  activity_transport_05: {
    title: 'Jeg gikk eller syklet til skolen i dag',
    description:
      'Kan logges på dager hvor du for det meste bruker ren muskelkraft, som å gå, skate, sykle eller benytter andre transportformer som ikke involverer strøm eller drivstoff for å komme til skole.',
    co2_explanation: `MARKDOWN:
## Fakta
- Ved å sykle eller gå kan du redusere antall biler på veien og dermed minske forurensning, støynivå, svevestøv, giftige gasser og samtidig bidra til et vakrere bymiljø. I tillegg vil andre digge deg for at du tar det beste valget for kloden vår.

## Hva vi antar
- Besparelsen er beregnet ved å se på forskjellen på hvordan en gjennomsnittsperson i ditt land kommer seg til skolen og en som benytter ren muskelkraft. 
- Fra reisevaneundersøkelsen kan vi finne avstander og transportmidler som en gjennomsnittsperson benytter til skolen. Den samme informasjonen er benyttet i vår personlige klimakalkulator. Her kan du finne mer informasjon og nøyaktig hvor mye du vil kunne spare gjennom året.`,
    health_explanation: `MARKDOWN:
## Fakta
- Ved å gå eller sykle til skolen vil du mest sannsynlig ha en bedre start på dagen samt snike inn en liten treningsøkt.
- Norsk helseinstitutt anbefaler minimum 30 minutter fysisk aktivitet hver dag.`,
    points_explanation: `MARKDOWN:
Poengene skal gjenspeile hvor stor påvirkning en aktivitet har på omverdenen.

**5 poeng - individuell påvirkning**<br/>
For eksempel: aktiviteter du kan gjøre på egen hånd uten å måtte kommunisere med eller påvirke andre. 

**10 poeng - sosial påvirkning**<br/>
For eksempel: Å gi komplimenter, snakke med noen, inspirere, motivere venner, familie, kollegaer eller en lokal butikk. 

**15 poeng - lokal påvirkning**<br/>
For eksempel: Å gi komplimenter, snakke med, inspirere eller motivere samfunnsorgan som organisasjoner, politikere, kommuner, osv.`,
  },
  activity_transport_06: {
    title: 'Jeg tok kollektivtransport til skolen i dag',
    description:
      'Kan logges på dager hvor du for det meste bruker offentlig transport som buss, trikk eller tog for å komme til skole.',
    co2_explanation: `MARKDOWN:
## Fakta
- Mange byer i verden har forbud mot biler i sentrum. Dette har ført til mindre utslipp, bedre luftkvalitet, mer liv i bykjernen og mindre ulykker.

## Hva vi antar 
- Besparelsen er beregnet ved å se på forskjellen på hvordan en gjennomsnittsperson i ditt land kommer seg til fritidsaktiviteter og en som benytter kollektivtransport. 
- Fra reisevaneundersøkelsen kan vi finne avstander og transportmidler som en gjennomsnittsperson benytter til fritidsaktiviteter. Den samme informasjonen er benyttet i vår personlige klimakalkulator. Her kan du finne mer informasjon og nøyaktig hvor mye du vil kunne spare gjennom året.

## Tilleggsinformasjon
- [https://www.theguardian.com/cities/2018/sep/18/paradise-life-spanish-city-banned-cars-pontevedra](https://www.theguardian.com/cities/2018/sep/18/paradise-life-spanish-city-banned-cars-pontevedra)`,
    health_explanation: `MARKDOWN:
## Fakta
- Å bruke kollektivtransport kan bidra til å fremme en mer aktiv livsstil og fjerne stress og angst tilknyttet bilkjøring.
- Kollektivtransport har gode, sosiale fordeler der du kan glede deg over en rask, sikker og billig transportmåte, og få kontakt med familie, venner og kolleger.  
- Kollektivtransport er en måte å sikre sosial likhet på, der lavtlønnede, arbeidsledige, eldre og personer med nedsatt funksjonsevne er spesielt utsatt for sosial isolasjon som følge av dårlige transportalternativer.`,
    points_explanation: `MARKDOWN:
Poengene skal gjenspeile hvor stor påvirkning en aktivitet har på omverdenen.

**5 poeng - individuell påvirkning**<br/>
For eksempel: aktiviteter du kan gjøre på egen hånd uten å måtte kommunisere med eller påvirke andre. 

**10 poeng - sosial påvirkning**<br/>
For eksempel: Å gi komplimenter, snakke med noen, inspirere, motivere venner, familie, kollegaer eller en lokal butikk. 

**15 poeng - lokal påvirkning**<br/>
For eksempel: Å gi komplimenter, snakke med, inspirere eller motivere samfunnsorgan som organisasjoner, politikere, kommuner, osv.`,
  },
  activity_transport_07: {
    title: 'Jeg kjørte elbil til jobb i dag',
    description:
      'Kan logges på dager hvor du for det meste bruker en elektrisk bil i motsetning til en bil som bruker fossilt brennstoff for å komme til jobb.',
    co2_explanation: `MARKDOWN:
## Fakta
- Energimiksen har stor betydning for hvor miljøvennlig elbilen er. Med strømmen vi benytter i Norden er utslippene fra kjøring 81 gram CO₂e per kilometer for en elbil, mens en bensin- eller diesebil har et utslipp på 250 gram CO₂e per kilometer. Disse tallene inkluderer også produksjonen av bilene.

## Hva vi antar
- Besparelsen er beregnet ved å se på forskjellen på hvordan en gjennomsnittsperson i ditt land kommer seg til jobb og en som benytter elbil. 
- Fra reisevaneundersøkelsen kan vi finne avstander og transportmidler som en gjennomsnittsperson benytter til arbeid. Den samme informasjonen er benyttet i vår personlige klimakalkulator. Her kan du finne mer informasjon og nøyaktig hvor mye du vil kunne spare gjennom året.`,
    health_explanation: `MARKDOWN:
## Fakta
- Fossilbiler produserer utslipp av et bredt spekter av helsepåvirkende miljøgifter. Partikler fra dieselbiler kan true menneskers helse fordi partiklene kan komme seg dypt inn i lungene og forårsake sykdommer som lungekreft og astma.
- Kjører du elbil bidrar du til mindre CO₂e-utslipp, renere luft og forhindrer sykdommer relatert til karbondioksid og andre miljøgifter fra fossile biler.
- Det er velkjent at CO₂e-utslipp fører til helseproblemer for mennesker.`,
    points_explanation: `MARKDOWN:
Poengene skal gjenspeile hvor stor påvirkning en aktivitet har på omverdenen.

**5 poeng - individuell påvirkning**<br/>
For eksempel: aktiviteter du kan gjøre på egen hånd uten å måtte kommunisere med eller påvirke andre. 

**10 poeng - sosial påvirkning**<br/>
For eksempel: Å gi komplimenter, snakke med noen, inspirere, motivere venner, familie, kollegaer eller en lokal butikk. 

**15 poeng - lokal påvirkning**<br/>
For eksempel: Å gi komplimenter, snakke med, inspirere eller motivere samfunnsorgan som organisasjoner, politikere, kommuner, osv.`,
  },
  activity_transport_08: {
    title: 'Jeg kjørte elbil til fritidsaktivitet i dag',
    description:
      'Kan logges på dager hvor du for det meste bruker en elektrisk bil i motsetning til en bil som bruker fossilt brennstoff for å komme til fritidsaktiviteter.',
    co2_explanation: `MARKDOWN:
## Fakta
- Energimiksen har stor betydning for hvor miljøvennlig elbilen er. Med strømmen vi benytter i Norden er utslippene fra kjøring 81 gram CO\u2082e per kilometer for en elbil, mens en bensin- eller diesebil har et utslipp på 250 gram per kilometer. Disse tallene inkluderer også produksjonen av bilene.

## Hva vi antar
- Besparelsen er beregnet ved å se på forskjellen på hvordan en gjennomsnittsperson i ditt land kommer seg til sine fritidsaktiviteter og en som benytter elbil. 
- Fra reisevaneundersøkelsen kan vi finne avstander og transportmidler som en gjennomsnittsperson benytter til arbeid. Den samme informasjonen er benyttet i vår personlige klimakalkulator. Her kan du finne mer informasjon og nøyaktig hvor mye du vil kunne spare gjennom året.`,
    health_explanation: `MARKDOWN:
## Fakta
- Fossilbiler produserer utslipp av et bredt spekter av helsepåvirkende miljøgifter. Partikler fra dieselbiler kan true menneskers helse fordi partiklene kan komme seg dypt inn i lungene og forårsake sykdommer som lungekreft og astma.
- Kjører du elbil bidrar du til mindre CO₂e-utslipp, renere luft og forhindrer sykdommer relatert til karbondioksid og andre miljøgifter fra fossile biler.
- Det er velkjent at CO₂e-utslipp fører til helseproblemer for mennesker.`,
    points_explanation: `MARKDOWN:
Poengene skal gjenspeile hvor stor påvirkning en aktivitet har på omverdenen.

**5 poeng - individuell påvirkning**<br/>
For eksempel: aktiviteter du kan gjøre på egen hånd uten å måtte kommunisere med eller påvirke andre. 

**10 poeng - sosial påvirkning**<br/>
For eksempel: Å gi komplimenter, snakke med noen, inspirere, motivere venner, familie, kollegaer eller en lokal butikk. 

**15 poeng - lokal påvirkning**<br/>
For eksempel: Å gi komplimenter, snakke med, inspirere eller motivere samfunnsorgan som organisasjoner, politikere, kommuner, osv.`,
  },
  activity_transport_09: {
    title: 'Jeg tok trappa i dag',
    description: 'Ta trappa i stedet for heisen gjennom hele dagen.',
    health_explanation: `MARKDOWN:
## Fakta
- Det å gå trapper forbrenner flere kalorier enn å jogge per minutt.
- Ved å gå trapper to ekstra minutt hver dag vil være nok til å hindre den vanlige vektøkningen når vi kommer litt opp i årene.

## HVORDAN FINNER VI UT HELSEEFFEKTEN
- Det å gå trappene er bra for å øke pulsen, bygge muskler og vil gi hjernen en liten pause noe som er bra for å redusere stress.

## Tilleggsinformasjon
- [https://www.stepjockey.com/health-benefits-of-stair-climbing](https://www.stepjockey.com/health-benefits-of-stair-climbing)
    `,
    points_explanation: `MARKDOWN:
Poengene skal gjenspeile hvor stor påvirkning en aktivitet har på omverdenen.

**5 poeng - individuell påvirkning**<br/>
For eksempel: aktiviteter du kan gjøre på egen hånd uten å måtte kommunisere med eller påvirke andre. 

**10 poeng - sosial påvirkning**<br/>
For eksempel: Å gi komplimenter, snakke med noen, inspirere, motivere venner, familie, kollegaer eller en lokal butikk. 

**15 poeng - lokal påvirkning**<br/>
For eksempel: Å gi komplimenter, snakke med, inspirere eller motivere samfunnsorgan som organisasjoner, politikere, kommuner, osv.`,
  },
  activity_transport_10: {
    title:
      'Jeg ga et kompliment til noen for å velge miljøvennlig transport i dag',
    description:
      'Inspirere andre til å fortsette med miljøhandlinger ved å annerkjenne deres positive adferd med et kompliment.',
    co2_explanation: `MARKDOWN:
## Fakta
- Kompliment må være ekte og du bør mene det for at det skal ha god effekt.

## DOKUMENTERT EFFEKT
- Vi har ikke kvantifisert effekten av dette enda, men vi vet at de fleste av oss sammenligner oss med hverandre, noe som betyr at et kompliment fra deg vil mest sannsynlig få den andre personen til å innse at du synes det er bra at de gjør noe miljøvennlig, noe som vil inspirerer dem til å fortsette.

## Tilleggsinformasjon
- [https://www.ted.com/talks/per_espen_stoknes_how_to_transform_apocalypse_fatigue_into_action_on_global_warming](https://www.ted.com/talks/per_espen_stoknes_how_to_transform_apocalypse_fatigue_into_action_on_global_warming)
    `,
    points_explanation: `MARKDOWN:
Poengene skal gjenspeile hvor stor påvirkning en aktivitet har på omverdenen.

**5 poeng - individuell påvirkning**<br/>
For eksempel: aktiviteter du kan gjøre på egen hånd uten å måtte kommunisere med eller påvirke andre. 

**10 poeng - sosial påvirkning**<br/>
For eksempel: Å gi komplimenter, snakke med noen, inspirere, motivere venner, familie, kollegaer eller en lokal butikk. 

**15 poeng - lokal påvirkning**<br/>
For eksempel: Å gi komplimenter, snakke med, inspirere eller motivere samfunnsorgan som organisasjoner, politikere, kommuner, osv.`,
  },
  activity_transport_11: {
    title:
      'Jeg diskuterte klima og transport med familie, venner, kollega eller på sosiale medier i dag',
    description:
      'Diskuter dette med andre for å vise at du bryr deg og ha som mål å forstå de andres synspunkter for å kunne bygge videre på det dere er enige om.',
    co2_explanation: `MARKDOWN:
## Fakta
- Vi må begynne å snakke sammen om bærekraft i så mange arena som mulig for å kunne finne løsninger sammen og for å putte temaet øverst på agendaen til folk flest. 
- Under mer info seksjonen kan du finne gode tips til hvordan du kan snakke om bærekraft på en måte som bygger bruer istedenfor murer.

## DOKUMENTERT EFFEKT
- Du kan ha en potensiell stor klimaeffekt av å dele dine tanker med andre siden klimaproblemet bare kan løses om vi samarbeider i mye større grad. Personen du snakker med kan bli inspirert til å snakke med andre, til å stemme på en grønn politikker, til å kjøpe bærekraftige produkter for å nevne noen potensielle effekter av denne handlingen.

## Tilleggsinformasjon
- [https://www.weforum.org/agenda/2018/06/how-to-talk-about-climate-change-5-tips-from-the-front-lines/](https://www.weforum.org/agenda/2018/06/how-to-talk-about-climate-change-5-tips-from-the-front-lines/)
- [https://www.ted.com/talks/per_espen_stoknes_how_to_transform_apocalypse_fatigue_into_action_on_global_warming](https://www.ted.com/talks/per_espen_stoknes_how_to_transform_apocalypse_fatigue_into_action_on_global_warming)
    `,
    points_explanation: `MARKDOWN:
Poengene skal gjenspeile hvor stor påvirkning en aktivitet har på omverdenen.

**5 poeng - individuell påvirkning**<br/>
For eksempel: aktiviteter du kan gjøre på egen hånd uten å måtte kommunisere med eller påvirke andre. 

**10 poeng - sosial påvirkning**<br/>
For eksempel: Å gi komplimenter, snakke med noen, inspirere, motivere venner, familie, kollegaer eller en lokal butikk. 

**15 poeng - lokal påvirkning**<br/>
For eksempel: Å gi komplimenter, snakke med, inspirere eller motivere samfunnsorgan som organisasjoner, politikere, kommuner, osv.`,
  },
  activity_transport_12: {
    title: 'Jeg hadde hjemmekontor i dag',
    description:
      'Kan logges på dager hvor du har jobbet hjemmefra istedet for å reise til kontoret.',
    co2_explanation: `MARKDOWN:
## Fakta
- Du kan spare over ett tonn CO₂e årlig sammenlignet med en gjennomsnittsnordmann som bor 10 kilometer fra jobb ved å gjøre dette hver dag. Det betyr at du kan spare 200 kg årlig ved å ha hjemmekontor en dag i uka.
- Mange vil oppleve å være mer produktive, mindre stresset og ha mer tid med familien ved å ha hjemmekontor en gang i blant.

## Hva vi antar
- Miljøbesparelsen er beregnet ved å se på forskjellen på hvordan en gjennomsnittsperson i din ditt land reiser til jobb og en som ikke reiser til jobb.
- Fra reisevaneundersøkelsen kan vi finne gjennomsnittlige avstander og transportmidler som en gjennomsnittsperson benytter til arbeid. Den samme informasjonen er benyttet i vår personlige klimakalkulator, slik at du kan finne mye mer informasjon der og finne ut akkurat hvor mye du vil kunne spare gjennom året.

## Tilleggsinformasjon
- [https://www.forbes.com/sites/kevinkruse/2012/12/18/benefits-working-from-home/#3b287c961d4c](https://www.forbes.com/sites/kevinkruse/2012/12/18/benefits-working-from-home/#3b287c961d4c)
    `,
    points_explanation: `MARKDOWN:
Poengene skal gjenspeile hvor stor påvirkning en aktivitet har på omverdenen.

**5 poeng - individuell påvirkning**<br/>
For eksempel: aktiviteter du kan gjøre på egen hånd uten å måtte kommunisere med eller påvirke andre. 

**10 poeng - sosial påvirkning**<br/>
For eksempel: Å gi komplimenter, snakke med noen, inspirere, motivere venner, familie, kollegaer eller en lokal butikk. 

**15 poeng - lokal påvirkning**<br/>
For eksempel: Å gi komplimenter, snakke med, inspirere eller motivere samfunnsorgan som organisasjoner, politikere, kommuner, osv.`,
  },
  activity_transport_13: {
    title: 'Jeg brukte digitale verktøy for samhandling i dag',
    description:
      'Benytt digitale samhandlingsverktøy, som videokonferanse, gruppechat eller digitale arbeidsrom etc. i dag.',
    co2_explanation: `MARKDOWN:
## Fakta
- Hvis du dropper en flytur til Europa med en typisk reiselengde på 1500km hver vei ville du spare ca 800 Kg CO\u2082e, noe som tilsvarer omtrent det samme besparelsen som om en gjennomsnittsnordmann blir veganer i ett år.

## EFFEKT
- I tilfeller hvor bruk av digitale samhandlingsverktøy erstatter en flyreise, vil du kunne spare nærmere 240kg CO\u2082e på en typisk innenlandsreise/reise i Skandinavia (snitt på 350 km / vei). På droppe en Europatur vil du spare 800kg CO\u2082e (nitt på 1500km / vei) mens ved å erstatte en global reise, vil du kunne spare hele 3350kg CO\u2082e tur/ retur (snitt på 7000km / vei).
- Det viktigste med denne konkurransen er å skape bevissthet på aktiviteter vi kan gjøre for å reise mer miljøvennlig. We've set up activities that most people can implement and calculate a daily saving so you can see the effects of what you do. Therefore, we collect the savings from this activity to the end result. This activity is all important, and therefore chosen to take it with as it creates awareness on own behavior.
- For å samle CO\u2082e knyttet til å redusere flyreiser, kan du registrere "Hvis jeg skulle bestille en lang jobbreise idag vil jeg først vurdere mulighetene for å minimere miljøpåvirkningen". Den kan registrere hver dag!
    `,
    points_explanation: `MARKDOWN:
Poengene skal gjenspeile hvor stor påvirkning en aktivitet har på omverdenen.

**5 poeng - individuell påvirkning**<br/>
For eksempel: aktiviteter du kan gjøre på egen hånd uten å måtte kommunisere med eller påvirke andre. 

**10 poeng - sosial påvirkning**<br/>
For eksempel: Å gi komplimenter, snakke med noen, inspirere, motivere venner, familie, kollegaer eller en lokal butikk. 

**15 poeng - lokal påvirkning**<br/>
For eksempel: Å gi komplimenter, snakke med, inspirere eller motivere samfunnsorgan som organisasjoner, politikere, kommuner, osv.`,
  },
  activity_transport_14: {
    title: 'Jeg gav et kompliment til noen for å velge videomøte i dag',
    description:
      'Inspirere andre til å fortsette å benytte video som møteform, ved å annerkjenne deres positive adferd med et kompliment.',
    co2_explanation: `MARKDOWN:
## Fakta
- Kompliment må være ekte og du bør mene det for at det skal ha god effekt.
- Det er anerkjent forskning at et kompliment kan forbedre selvbildet og påvirke hvor godt vi og de vi har rundt oss presterer. Et kompliment kan revolusjonere personlig atferd og bidra til å øke miljø og klimavennlige aktiviteter.
- Å gi et kompliment til andre er et kraftig virkemiddel for å oppfordre familie, venner og sammfunnet til å bruke mer miljø og klimavennlige alternativer, ved å gi dem sosial annerkjennelse for at de har lagt en innsats i å spare kloden.

## EFFEKT
- Vi vet at de fleste av oss sammenligner oss med hverandre, noe som betyr at et kompliment fra deg vil mest sannsynlig få den andre personen til å innse at du synes det er bra at de gjør noe miljøvennlig, noe som vil inspirerer dem til å fortsette.
    `,
    points_explanation: `MARKDOWN:
Poengene skal gjenspeile hvor stor påvirkning en aktivitet har på omverdenen.

**5 poeng - individuell påvirkning**<br/>
For eksempel: aktiviteter du kan gjøre på egen hånd uten å måtte kommunisere med eller påvirke andre. 

**10 poeng - sosial påvirkning**<br/>
For eksempel: Å gi komplimenter, snakke med noen, inspirere, motivere venner, familie, kollegaer eller en lokal butikk. 

**15 poeng - lokal påvirkning**<br/>
For eksempel: Å gi komplimenter, snakke med, inspirere eller motivere samfunnsorgan som organisasjoner, politikere, kommuner, osv.`,
  },
  activity_transport_15: {
    title:
      'Jeg gav et kompliment til noen for å velge kollektivt til lange jobbreiser i dag',
    description:
      'Inspirere andre til å fortsette å reise kollektivt til lange jobbreiser, ved å annerkjenne deres positive adferd med et kompliment.',
    co2_explanation: `MARKDOWN:
## Fakta
- Kompliment må være ekte og du bør mene det for at det skal ha god effekt.
- Det er anerkjent i forskning at et kompliment kan forbedre selvbildet og hvor godt vi og de vi har rundt oss presterer. Et kompliment kan revolusjonere personlig atferd og bidra til å øke miljø og klimavennlige aktiviteter.
- Å gi et kompliment til andre er en sterk måte å oppfordre familie, venner og sammfunnet til å bruke mer miljø og klimavennlige alternativer, ved å gi dem sosial annerkjennelse for at de har lagt en innsats i å spare kloden. 

## EFFEKT
- Vi vet at de fleste av oss sammenligner oss med hverandre, noe som betyr at et kompliment fra deg vil mest sannsynlig få den andre personen til å innse at du synes det er bra at de gjør noe miljøvennlig, noe som vil inspirerer dem til å fortsette.
    `,
    points_explanation: `MARKDOWN:
Poengene skal gjenspeile hvor stor påvirkning en aktivitet har på omverdenen.

**5 poeng - individuell påvirkning**<br/>
For eksempel: aktiviteter du kan gjøre på egen hånd uten å måtte kommunisere med eller påvirke andre. 

**10 poeng - sosial påvirkning**<br/>
For eksempel: Å gi komplimenter, snakke med noen, inspirere, motivere venner, familie, kollegaer eller en lokal butikk. 

**15 poeng - lokal påvirkning**<br/>
For eksempel: Å gi komplimenter, snakke med, inspirere eller motivere samfunnsorgan som organisasjoner, politikere, kommuner, osv.`,
  },
  activity_transport_16: {
    title: 'Jeg samkjørte til jobb i dag',
    description: 'Du kan registrere hver dag du samkjører til jobb',
    points_explanation: `MARKDOWN:
Poengene skal gjenspeile hvor stor påvirkning en aktivitet har på omverdenen.

**5 poeng - individuell påvirkning**<br/>
For eksempel: aktiviteter du kan gjøre på egen hånd uten å måtte kommunisere med eller påvirke andre. 

**10 poeng - sosial påvirkning**<br/>
For eksempel: Å gi komplimenter, snakke med noen, inspirere, motivere venner, familie, kollegaer eller en lokal butikk. 

**15 poeng - lokal påvirkning**<br/>
For eksempel: Å gi komplimenter, snakke med, inspirere eller motivere samfunnsorgan som organisasjoner, politikere, kommuner, osv.`,
    biodiversity_explanation: `MARKDOWN:
## Fakta
- Bykjøring resulterer i mer mikroplastutslipp enn å kjøre andre steder? Dette fordi du akselererer, bremser og dreier mer enn du gjør på motorvei og motorveikjøring.

## Tilleggsinformasjon
- [https://www.miljodirektoratet.no/globalassets/publikasjoner/M959/M959.pdf](https://www.miljodirektoratet.no/globalassets/publikasjoner/M959/M959.pdf)`,
  },
  activity_transport_17: {
    title: 'Jeg brukte ingen transportmidler i dag',
    description: 'Du kan registrere hver dag du ikke bruker transportmidler',
    points_explanation: `MARKDOWN:
Poengene skal gjenspeile hvor stor påvirkning en aktivitet har på omverdenen.

**5 poeng - individuell påvirkning**<br/>
For eksempel: aktiviteter du kan gjøre på egen hånd uten å måtte kommunisere med eller påvirke andre. 

**10 poeng - sosial påvirkning**<br/>
For eksempel: Å gi komplimenter, snakke med noen, inspirere, motivere venner, familie, kollegaer eller en lokal butikk. 

**15 poeng - lokal påvirkning**<br/>
For eksempel: Å gi komplimenter, snakke med, inspirere eller motivere samfunnsorgan som organisasjoner, politikere, kommuner, osv.`,
    co2_explanation: `MARKDOWN:
## Fakta
- Transportsektoren er en av de største utslippssektorene i verden. Transport utgjør rundt 17 prosent av klimafotavtrykket vårt i snitt. Å unngå å kjøre bil eller ta fly når du har mer miljøvennlige alternativer bidrar stort!`,
    biodiversity_explanation: `MARKDOWN:
## Fakta
- Bykjøring fører til mer mikroplastutslipp enn kjøring andre steder. Dette er fordi man akselererer, bremser og svinger mer enn man gjør på motorveier og landeveiskjøring.

## Tilleggsinformasjon
- [https://www.miljodirektoratet.no/globalassets/publikasjoner/M959/M959.pdf](https://www.miljodirektoratet.no/globalassets/publikasjoner/M959/M959.pdf)`,
  },
  activity_transport_18: {
    title: 'Jeg samkjørte til skolen i dag',
    description: 'Du kan registrere hver dag du samkjører til skolen',
    points_explanation: `MARKDOWN:
Poengene skal gjenspeile hvor stor påvirkning en aktivitet har på omverdenen.

**5 poeng - individuell påvirkning**<br/>
For eksempel: aktiviteter du kan gjøre på egen hånd uten å måtte kommunisere med eller påvirke andre. 

**10 poeng - sosial påvirkning**<br/>
For eksempel: Å gi komplimenter, snakke med noen, inspirere, motivere venner, familie, kollegaer eller en lokal butikk. 

**15 poeng - lokal påvirkning**<br/>
For eksempel: Å gi komplimenter, snakke med, inspirere eller motivere samfunnsorgan som organisasjoner, politikere, kommuner, osv.`,
    biodiversity_explanation: `MARKDOWN:
## Fakta
- Bykjøring resulterer i mer mikroplastutslipp enn å kjøre andre steder? Dette fordi du akselererer, bremser og dreier mer enn du gjør på motorvei og motorveikjøring.

## Tilleggsinformasjon
- [https://www.miljodirektoratet.no/globalassets/publikasjoner/M959/M959.pdf](https://www.miljodirektoratet.no/globalassets/publikasjoner/M959/M959.pdf)`,
  },
  activity_transport_19: {
    title: 'Jeg kjørte miljøvennlig i dag',
    description:
      'Kan logges på dager du kjørte bilen din på en miljøvennlig måte.',
    co2_explanation: `MARKDOWN:
## Fakta
- Økokjøring er et sett med handlinger som en bilfører kan ta for å påvirke drivstofføkonomien og utslippsintensiteten til et kjøretøy, og redusere dets miljøpåvirkning. Disse handlingene inkluderer: strategiske beslutninger (vanlig kjøretøyvedlikehold, optimalt dekktrykk), taktiske beslutninger (rutevalg og kjøretøybelastning),1 og operasjonelle beslutninger (kjøring med lavt turtall, jevn hastighet, akselerer/retarder jevnt)
- Rapporterte fordeler ved økokjøring varierer mellom 5-30 %, noe som gjør det til et attraktivt alternativ for sjåfører som ønsker å redusere drivstoffkostnadene og klimagassutslippene.

##  Hva vi antar
- Vi antar økokjøring kan resultere i drivstoffeffektivitetsforbedringer på 4,8 %. Vi anslår gjennomsnittspersonens utslipp fra bilkjøring basert på kjøremønsteret og reduserer utslippene med 4,8 % for å ta hensyn til effektivitetsforbedringene fra økokjøring.`,
    points_explanation: `MARKDOWN:
Poengene skal gjenspeile hvor stor påvirkning en aktivitet har på omverdenen.

**5 poeng - individuell påvirkning**<br/>
For eksempel: aktiviteter du kan gjøre på egen hånd uten å måtte kommunisere med eller påvirke andre. 

**10 poeng - sosial påvirkning**<br/>
For eksempel: Å gi komplimenter, snakke med noen, inspirere, motivere venner, familie, kollegaer eller en lokal butikk. 

**15 poeng - lokal påvirkning**<br/>
For eksempel: Å gi komplimenter, snakke med, inspirere eller motivere samfunnsorgan som organisasjoner, politikere, kommuner, osv.`,
  },
  activity_transport_20: {
    title: 'Jeg syklet på elsykkel til jobb i dag',
    description:
      'Logges på dager når du, istedenfor å kjøre bil eller ta buss, tar elsykkel til jobb',
    co2_explanation: `MARKDOWN:
## Fakta
- 59% av reiser mellom 1 og 2,9 km blir foretatt med bil. Å bruke elsykkel i steden vil både spare klimaet og bekjempe fysisk inaktivitet!
- I dag er det minst 176 000 elsykler i Norge.

## Hva vi antar
- Vi regner fotavtrykket til en elsykkel per kilometer brukt, basert på strømforbruk og produksjonsutslippene.
- Dette trekkes fra fotavtrykket til gjennomsnittspendleren, som bruker en kombinasjon av bil og buss
- Gjennomsnittspendleren reiser 10 km til og fra jobb. Om du bor nærmere er besparelse litt mindre mens den er mer om du bor lengre unna

## Tilleggsinformasjon
- [https://www.framtiden.no/tips/ti-gode-grunner-til-aa-velge-elsykkel](https://www.framtiden.no/tips/ti-gode-grunner-til-aa-velge-elsykkel)`,
    points_explanation: `MARKDOWN:
Poengene skal gjenspeile hvor stor påvirkning en aktivitet har på omverdenen.

**5 poeng - individuell påvirkning**<br/>
For eksempel: aktiviteter du kan gjøre på egen hånd uten å måtte kommunisere med eller påvirke andre. 

**10 poeng - sosial påvirkning**<br/>
For eksempel: Å gi komplimenter, snakke med noen, inspirere, motivere venner, familie, kollegaer eller en lokal butikk. 

**15 poeng - lokal påvirkning**<br/>
For eksempel: Å gi komplimenter, snakke med, inspirere eller motivere samfunnsorgan som organisasjoner, politikere, kommuner, osv.`,
  },
  activity_transport_21: {
    title: 'Jeg syklet på elsykkel til skolen i dag',
    description:
      'Logges på dager når du, istedenfor å kjøre bil eller ta buss, tar elsykkel til skole',
    co2_explanation: `MARKDOWN:
## Fakta
- 59% av reiser mellom 1 og 2,9 km blir foretatt med bil. Å bruke elsykkel i steden vil både spare klimaet og bekjempe fysisk inaktivitet!
- I dag er det minst 176 000 elsykler i Norge.

## Hva vi antar
- Vi regner fotavtrykket til en elsykkel per kilometer brukt, basert på strømforbruk og produksjonsutslippene.
- Dette trekkes fra fotavtrykket til gjennomsnittspendleren, som bruker en kombinasjon av bil og buss
- Gjennomsnittspendleren reiser 10 km til og fra jobb. Om du bor nærmere er besparelse litt mindre mens den er mer om du bor lengre unna

## Tilleggsinformasjon
- [https://www.framtiden.no/tips/ti-gode-grunner-til-aa-velge-elsykkel](https://www.framtiden.no/tips/ti-gode-grunner-til-aa-velge-elsykkel)`,
    points_explanation: `MARKDOWN:
Poengene skal gjenspeile hvor stor påvirkning en aktivitet har på omverdenen.

**5 poeng - individuell påvirkning**<br/>
For eksempel: aktiviteter du kan gjøre på egen hånd uten å måtte kommunisere med eller påvirke andre. 

**10 poeng - sosial påvirkning**<br/>
For eksempel: Å gi komplimenter, snakke med noen, inspirere, motivere venner, familie, kollegaer eller en lokal butikk. 

**15 poeng - lokal påvirkning**<br/>
For eksempel: Å gi komplimenter, snakke med, inspirere eller motivere samfunnsorgan som organisasjoner, politikere, kommuner, osv.`,
  },
  activity_transport_22: {
    title: 'Jeg syklet på elsykkel til en fritidsaktivitet i dag',
    description:
      'Logges på dager når du, istedenfor å kjøre bil eller ta buss, tar elsykkel til en fritidsaktivitet',
    co2_explanation: `MARKDOWN:
## Fakta
- Med elsykkel får du hjelp av motoren til å holde opp til 25 km/t, slik at du kan reise miljøvennlig uten å bli svett.
- Det finnes ulike støtteordninger som gir tilskudd for å kjøpe elsykkel - sjekk om dette finnes i din kommune!

## Hva vi antar
- Vi regner fotavtrykket til en elsykkel per kilometer brukt, basert på strømforbruk og produksjonsutslippene (hovedsakelig knyttet til batteriet).
- Dette trekkes fra fotavtrykket til en gjennomsnittspersons fritidsreiser, som består av en kombinasjon av bil og buss

## Tilleggsinformasjon
- [https://www.framtiden.no/tips/ti-gode-grunner-til-aa-velge-elsykkel](https://www.framtiden.no/tips/ti-gode-grunner-til-aa-velge-elsykkel)`,
    points_explanation: `MARKDOWN:
Poengene skal gjenspeile hvor stor påvirkning en aktivitet har på omverdenen.

**5 poeng - individuell påvirkning**<br/>
For eksempel: aktiviteter du kan gjøre på egen hånd uten å måtte kommunisere med eller påvirke andre. 

**10 poeng - sosial påvirkning**<br/>
For eksempel: Å gi komplimenter, snakke med noen, inspirere, motivere venner, familie, kollegaer eller en lokal butikk. 

**15 poeng - lokal påvirkning**<br/>
For eksempel: Å gi komplimenter, snakke med, inspirere eller motivere samfunnsorgan som organisasjoner, politikere, kommuner, osv.`,
  },
};
